import type {
  EditorRecreateChanges,
  EditorRecreateOptions,
} from "@blings/blings-player/editor-tools";
import type { ISdkParams } from "@blings/blings-player/lib/src/SDK/sdk.api";
import type { IVideoPart } from "@blings/blings-player";
import { isEqual } from "lodash";
import { cleanEmptyStringInValue } from "../helpers/objects";

export type StudioScenePatch = {
  name?: string;
  json?: unknown;
  modsArr?: unknown[];
  fonts?: IVideoPart["fonts"];
};

type JsonAnimationSize = {
  w?: number;
  h?: number;
};

type ProjectVideoPart = StudioScenePatch & Record<string, unknown>;
type AnimationSceneMarker = {
  cm: string;
  dr: number;
  tm: number;
};
type AnimationWithSceneRange = JsonAnimationSize &
  Record<string, unknown> & {
    ip?: number;
    op?: number;
    scenes?: unknown[];
  };

export type PlayerRunConfig = {
  projectId?: string | null;
  JsonConfig?: unknown[];
  jsonFile?: JsonAnimationSize & Record<string, unknown>;
  autoplay?: boolean;
  posterFrame?: number;
  startFrame?: number;
  scenes?: unknown[];
  rendererType?: string;
  videoData?: unknown;
  liveControlData?: unknown;
  videoParts?: ProjectVideoPart[];
  cb?: (player: unknown) => void;
  onError?: (error: unknown) => void;
  assetChanges?: unknown;
  playerVersion?: number;
  experiments?: unknown;
  variantSelectionSet?: unknown;
  forceRecreate?: boolean;
  flowDiagram?: unknown;
  fontOverrides?: unknown;
  animationChange?: unknown;
};

export const PLAYER_RECREATE_DEBOUNCE_MS = 450;

const CONCAT_SCOPED_LAYER_MOD_TYPES = new Set([
  "embedElement",
  "interactiveConcat",
  "interactiveForm",
  "interactiveGoto",
  "interactiveInput",
  "interactiveJSExpression",
  "interactiveOpen",
]);

const objectIdentityIds = new WeakMap<object, number>();
let nextObjectIdentityId = 1;

export const cloneForPlayer = <T>(value: T): T =>
  value == null ? value : JSON.parse(JSON.stringify(value));

const getSceneName = (scene: unknown): string | undefined => {
  if (typeof scene === "string") {
    return scene;
  }
  if (!scene || typeof scene !== "object") {
    return undefined;
  }

  const sceneRecord = scene as Record<string, unknown>;
  return typeof sceneRecord.id === "string"
    ? sceneRecord.id
    : typeof sceneRecord.name === "string"
    ? sceneRecord.name
    : undefined;
};

const getPrimarySceneName = (config: PlayerRunConfig): string | undefined =>
  getSceneName(config?.scenes?.[0]);

const getObjectIdentity = (value: unknown) => {
  if (!value || typeof value !== "object") {
    return value ?? null;
  }

  const objectValue = value as object;
  const existing = objectIdentityIds.get(objectValue);
  if (existing) {
    return existing;
  }

  const nextId = nextObjectIdentityId++;
  objectIdentityIds.set(objectValue, nextId);
  return nextId;
};

export const createStudioScenePatch = (
  config: PlayerRunConfig
): StudioScenePatch => {
  const sceneName = getPrimarySceneName(config);
  const videoPart = config?.videoParts?.find?.((vp) => vp?.name === sceneName);
  const modsArr = cloneForPlayer(videoPart?.modsArr ?? config?.JsonConfig);

  return {
    name: sceneName,
    json: cloneForPlayer(videoPart?.json ?? config?.jsonFile),
    modsArr: scopeCurrentScenePrimaryConnectorTargets(modsArr),
    fonts: cloneForPlayer(videoPart?.fonts),
  };
};

export function scopeCurrentScenePrimaryConnectorTargets<T>(modsArr: T): T {
  if (!Array.isArray(modsArr)) {
    return modsArr;
  }

  return modsArr.map((mod) => {
    if (!shouldScopeCurrentScenePrimaryTarget(mod)) {
      return mod;
    }

    return {
      ...(mod as Record<string, unknown>),
      assetId: undefined,
    };
  }) as T;
}

function shouldScopeCurrentScenePrimaryTarget(mod: unknown): boolean {
  if (!isObjectRecord(mod)) {
    return false;
  }

  const type = mod.type;
  if (typeof type !== "string" || !CONCAT_SCOPED_LAYER_MOD_TYPES.has(type)) {
    return false;
  }

  if (!hasLayerName(mod.layerName)) {
    return false;
  }

  return !Object.prototype.hasOwnProperty.call(mod, "assetId");
}

function hasLayerName(layerName: unknown): layerName is string {
  return typeof layerName === "string" && layerName.length > 0;
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object";

const getAnimationDurationFrames = (animation: AnimationWithSceneRange) => {
  const ip = typeof animation.ip === "number" ? animation.ip : 0;
  if (typeof animation.op === "number") {
    return Math.max(1, animation.op - ip);
  }

  return 1;
};

export const createFallbackAnimationScene = (
  animation: AnimationWithSceneRange,
  config: PlayerRunConfig
): AnimationSceneMarker => ({
  cm: getPrimarySceneName(config) || "Scene 1",
  dr: getAnimationDurationFrames(animation),
  tm: 0,
});

export const ensureAnimationScenes = <T>(
  animation: T,
  config: PlayerRunConfig
): T => {
  if (!isObjectRecord(animation)) {
    return animation;
  }

  const animationWithScenes = animation as AnimationWithSceneRange;
  if (
    Array.isArray(animationWithScenes.scenes) &&
    animationWithScenes.scenes.length
  ) {
    return animation;
  }

  animationWithScenes.scenes = [
    createFallbackAnimationScene(animationWithScenes, config),
  ];
  return animation;
};

export const applyStudioScenePatchBeforeConcat = (
  patch: StudioScenePatch | undefined,
  videoParts: StudioScenePatch[]
) => {
  if (!patch?.name) {
    return;
  }

  const target = videoParts.find((videoPart) => videoPart?.name === patch.name);
  if (!target) {
    return;
  }

  if (typeof patch.json !== "undefined") {
    target.json = patch.json;
  }
  if (typeof patch.modsArr !== "undefined") {
    target.modsArr = patch.modsArr;
  }
  if (typeof patch.fonts !== "undefined") {
    target.fonts = patch.fonts;
  }
};

const getProjectVideoPartCacheInputs = (
  videoParts: ProjectVideoPart[] | undefined,
  primarySceneName?: string
) =>
  videoParts?.map((videoPart) => {
    if (videoPart?.name !== primarySceneName) {
      return videoPart;
    }

    const { json, modsArr, mods, ...stableVideoPart } = videoPart || {};
    return {
      ...stableVideoPart,
      json: getObjectIdentity(json),
      modsArr,
      mods,
    };
  });

const getProjectCacheInputs = (config: PlayerRunConfig) => ({
  projectId: config?.projectId,
  primarySceneName: getPrimarySceneName(config),
  videoParts: getProjectVideoPartCacheInputs(
    config?.videoParts,
    getPrimarySceneName(config)
  ),
  assetChanges: config?.assetChanges,
});

export const getNormalizedProjectCacheInputs = (config: PlayerRunConfig) =>
  cloneForPlayer(getProjectCacheInputs(config));

const getRatio = (config: PlayerRunConfig) => {
  const width = config.jsonFile?.w;
  const height = config.jsonFile?.h;
  return typeof width === "number" && typeof height === "number"
    ? width / height
    : undefined;
};

const getRecreateParamsCacheInputs = (config: PlayerRunConfig) =>
  cloneForPlayer({
    experiments: config?.experiments,
    variantSelectionSet: config?.variantSelectionSet,
    flowDiagram: config?.flowDiagram,
    settings: {
      autoplay: config?.autoplay,
      posterFrame: config?.posterFrame,
      ratio: getRatio(config),
      rendererType: config?.rendererType,
      fontOverrides: config?.fontOverrides,
      jsonConfig: config?.JsonConfig,
    },
  });

const getRecreateDataCacheInputs = (config: PlayerRunConfig) =>
  cleanEmptyStringInValue(cloneForPlayer(config?.videoData || {}));

const getRecreateScenesCacheInputs = (config: PlayerRunConfig) =>
  cloneForPlayer(config?.scenes);

const hasObjectKeys = (value: unknown) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;

export const getRecreateCacheInputs = (config: PlayerRunConfig) => ({
  animation: getObjectIdentity(config?.jsonFile),
  params: getRecreateParamsCacheInputs(config),
  data: getRecreateDataCacheInputs(config),
  scenes: getRecreateScenesCacheInputs(config),
});

type RecreateCacheInputs = ReturnType<typeof getRecreateCacheInputs>;

export const createConfigSignature = (config: PlayerRunConfig) =>
  JSON.stringify({
    projectId: config?.projectId,
    playerVersion: config?.playerVersion,
    primarySceneName: getPrimarySceneName(config),
    scenes: config?.scenes?.map?.(getSceneName),
    autoplay: config?.autoplay,
    posterFrame: config?.posterFrame,
    startFrame: config?.startFrame,
    rendererType: config?.rendererType,
    jsonFile: getObjectIdentity(config?.jsonFile),
    JsonConfig: config?.JsonConfig,
    videoData: config?.videoData,
    liveControlData: config?.liveControlData,
    experiments: config?.experiments,
    variantSelectionSet: config?.variantSelectionSet,
    flowDiagram: getObjectIdentity(config?.flowDiagram),
    fontOverrides: config?.fontOverrides,
    project: getNormalizedProjectCacheInputs(config),
  });

const shouldIncludeProjectInRecreate = (
  currentConfig: PlayerRunConfig,
  previousProjectCacheInputs: unknown
) =>
  !previousProjectCacheInputs ||
  !isEqual(
    getNormalizedProjectCacheInputs(currentConfig),
    previousProjectCacheInputs
  );

export const buildRecreateChanges = (
  playerParams: ISdkParams,
  config: PlayerRunConfig,
  previousProjectCacheInputs: unknown,
  previousRecreateCacheInputs: RecreateCacheInputs | undefined
): EditorRecreateChanges => {
  const changes: EditorRecreateChanges = {};
  const nextRecreateCacheInputs = getRecreateCacheInputs(config);
  const dataChanged =
    !previousRecreateCacheInputs ||
    !isEqual(nextRecreateCacheInputs.data, previousRecreateCacheInputs.data);
  const shouldRefreshProjectForData =
    dataChanged && Boolean(config?.JsonConfig?.length);
  const shouldIncludeProject =
    shouldIncludeProjectInRecreate(config, previousProjectCacheInputs) ||
    shouldRefreshProjectForData;
  const inferredAnimationChange =
    previousRecreateCacheInputs &&
    !isEqual(
      nextRecreateCacheInputs.animation,
      previousRecreateCacheInputs.animation
    )
      ? config.jsonFile
      : undefined;
  const animationChange = shouldIncludeProject
    ? undefined
    : config.animationChange || inferredAnimationChange;

  if (animationChange && !config.forceRecreate) {
    changes.animation = ensureAnimationScenes(
      cloneForPlayer(animationChange),
      config
    ) as EditorRecreateChanges["animation"];
  }

  if (
    shouldIncludeProject ||
    !previousRecreateCacheInputs ||
    !isEqual(nextRecreateCacheInputs.params, previousRecreateCacheInputs.params)
  ) {
    changes.params = {
      experiments: playerParams.experiments,
      variantSelectionSet: playerParams.variantSelectionSet,
      flowDiagram: playerParams.flowDiagram,
      settings: playerParams.settings,
      rest: playerParams.rest,
    };
  }

  const shouldRefreshDataWithProject =
    shouldIncludeProject && hasObjectKeys(playerParams.data);

  if (dataChanged || shouldRefreshDataWithProject) {
    changes.data = playerParams.data;
  }

  if (
    !previousRecreateCacheInputs ||
    !isEqual(nextRecreateCacheInputs.scenes, previousRecreateCacheInputs.scenes)
  ) {
    changes.scenes = playerParams.scenes;
  }

  if (shouldIncludeProject) {
    changes.project = playerParams.project;
  }

  return changes;
};

export const isAnimationOnlyRecreate = (changes: EditorRecreateChanges) =>
  Boolean(changes.animation) &&
  Object.keys(changes).every((key) => key === "animation");

export const omitAnimationChange = (
  changes: EditorRecreateChanges
): EditorRecreateChanges => {
  const { animation: _animation, ...fallbackChanges } = changes;
  return fallbackChanges;
};

export const getRecreateOptions = (
  config: PlayerRunConfig,
  forceRecreate?: boolean
): EditorRecreateOptions => {
  if (!forceRecreate) {
    return {
      frame:
        typeof config.startFrame === "number" ? config.startFrame : "current",
      playback: "keep",
      cover: "hide",
    };
  }

  return {
    frame: "poster",
    playback: config.autoplay ? "play" : "pause",
    cover: "hide",
  };
};
